import { Check, X, Spinner } from "@phosphor-icons/react";
import { useState, useRef } from "react";

interface TicketInlineEditProps {
  ticketId: string | undefined;
  ticketTitle: string;
  onSave: (title: string) => Promise<void>;
  onCancel: () => void;
}

export function TicketInlineEdit({
  ticketId,
  ticketTitle,
  onSave,
  onCancel,
}: TicketInlineEditProps) {
  const [editValue, setEditValue] = useState(ticketTitle);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editValue.trim()) return;
    setIsSubmitting(true);
    try {
      await onSave(editValue);
      // Close the edit form on successful save
      onCancel();
    } catch (err) {
      console.error("Failed to rename ticket:", err);
      // Keep editing state active so user can retry
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onCancel();
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Check if the new focus target is within the same form
    const relatedTarget = e.relatedTarget as HTMLElement;
    const formElement = e.currentTarget.closest("form");

    // Don't close if the new focus target is within the same form
    if (relatedTarget && formElement && formElement.contains(relatedTarget)) {
      return;
    }

    if (!isSubmitting) {
      onCancel();
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex min-w-0 items-center"
      data-testid={`breadcrumb-ticket-edit-form-${ticketId || "unknown"}`}
    >
      <input
        ref={inputRef}
        className="bg-white font-inherit m-0 w-full min-w-0 rounded border border-puntt-blue-9 p-1 text-[20px] font-normal leading-[28px] tracking-[-0.08px] shadow-sm outline-none focus:ring-0"
        value={editValue}
        autoFocus
        onFocus={(e) => e.target.select()}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        data-testid={`breadcrumb-ticket-edit-input-${ticketId || "unknown"}`}
        disabled={isSubmitting}
      />
      <button
        type="submit"
        className="hover:bg-green-50 ml-1 flex cursor-pointer items-center justify-center rounded border-none bg-transparent p-1 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Save ticket name"
        tabIndex={-1}
        disabled={!editValue.trim() || isSubmitting}
        onMouseDown={(e) => {
          // Prevent the input from losing focus when clicking the submit button
          // Only prevent default if the button is enabled (not disabled)
          const isDisabled = !editValue.trim() || isSubmitting;
          if (!isDisabled) {
            e.preventDefault();
          }
        }}
        data-testid={`breadcrumb-ticket-save-button-${ticketId || "unknown"}`}
      >
        {isSubmitting ? (
          <Spinner size="1" />
        ) : (
          <Check size={18} className="text-green-600" />
        )}
      </button>
      <button
        type="button"
        className="hover:bg-red-50 ml-1 flex cursor-pointer items-center justify-center rounded border-none bg-transparent p-1 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Cancel ticket name edit"
        onClick={onCancel}
        tabIndex={-1}
        disabled={isSubmitting}
        data-testid={`breadcrumb-ticket-cancel-button-${ticketId || "unknown"}`}
      >
        <X size={18} className="text-red-600" />
      </button>
    </form>
  );
}
